import * as APITypes from "~/API";
import type { LeaseListLoaderResult, LeaseListLoaderResultItem } from "~/services/apiClient";

export type HomePendingLeaseKind =
  | "submitted"
  | "under_review"
  | "approved_pre_move_in";

export type HomePendingLease = {
  id: string;
  label: string;
  kind: HomePendingLeaseKind;
  moveInDate: string | null;
};

const KIND_PRIORITY: Record<HomePendingLeaseKind, number> = {
  under_review: 0,
  submitted: 1,
  approved_pre_move_in: 2,
};

function entityLabel(item: LeaseListLoaderResultItem): string {
  const name = item.entityDetails?.name?.trim();
  if (name) return name;
  return item.leaseInfo.id;
}

function isApprovedPreMoveIn(item: LeaseListLoaderResultItem): boolean {
  const status = item.leaseInfo?.status;
  if (status === APITypes.LeaseStatusType.Active) return false;
  if (
    status === APITypes.LeaseStatusType.Terminated ||
    status === APITypes.LeaseStatusType.Rejected ||
    status === APITypes.LeaseStatusType.Expired
  ) {
    return false;
  }
  return item.application?.status === APITypes.ApplicationStatusType.Approved;
}

function toPendingLease(
  item: LeaseListLoaderResultItem,
  kind: HomePendingLeaseKind
): HomePendingLease {
  const startDate = item.leaseInfo?.startDate?.trim() || null;
  return {
    id: item.leaseInfo.id,
    label: entityLabel(item),
    kind,
    moveInDate: startDate,
  };
}

function sortPendingLeases(items: HomePendingLease[]): HomePendingLease[] {
  return [...items].sort((a, b) => {
    const priorityDiff = KIND_PRIORITY[a.kind] - KIND_PRIORITY[b.kind];
    if (priorityDiff !== 0) return priorityDiff;

    if (a.moveInDate && b.moveInDate) {
      return a.moveInDate.localeCompare(b.moveInDate);
    }
    if (a.moveInDate) return -1;
    if (b.moveInDate) return 1;
    return a.label.localeCompare(b.label);
  });
}

/** Leases that need attention before move-in / while application is in flight. */
export function buildHomePendingLeases(
  data: LeaseListLoaderResult | undefined
): HomePendingLease[] {
  if (!data) return [];

  const pending: HomePendingLease[] = [];

  for (const item of data.leaseListUnderReview ?? []) {
    pending.push(toPendingLease(item, "under_review"));
  }
  for (const item of data.leaseListSubmitted ?? []) {
    pending.push(toPendingLease(item, "submitted"));
  }
  for (const item of data.leaseListApproved ?? []) {
    if (isApprovedPreMoveIn(item)) {
      pending.push(toPendingLease(item, "approved_pre_move_in"));
    }
  }

  return sortPendingLeases(pending);
}
